import { inject, injectable } from 'inversify'
import { Subscription } from 'rxjs'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { Aircraft } from '@/domain/models/Aircraft'
import { Waypoint } from '@/domain/models/Waypoint'
import { Result } from '@/domain/protocols/Result'
import { FlightState, FlightStateProps } from '@/domain/states/FlightState'
import {
  RouteNavigationState,
  RouteStateUserToWaypointsInfo,
  RouteStateVerticalInfo
} from '@/domain/states/RouteNavigationState'
import { RouteState, RouteStateProps } from '@/domain/states/RouteState'
import { GetWaypointAltitudeUseCase } from '@/domain/useCases/Route/getWaypointAltitude'

import { IService } from '../IService'

import { LocalLogger } from '@/infra/logger/LocalLogger'
import { magneticBearing, nmDistance } from '@/utils/coordinates'
import { isSet } from '@/utils/validation'
import {
  calculateDistanceToTocInNM,
  calculateDistanceToTodInNM,
  calculateEteFromTocToTodInSeconds,
  calculateEteToTocInSeconds,
  calculateFuelConsumptionPerHour,
  simulateWaypointsInfo
} from '@/utils/vertical'

@injectable()
export class RouteStateSideEffects implements IService {
  private changeRouteSubject: Subscription
  private changeFlightStateSubject: Subscription

  private altitudeRecalculationCount = 0
  private maxAltitudeRecalculationCount = 3

  constructor(
    @inject(InjectionTokens.RouteState) private routeState: RouteState,
    @inject(InjectionTokens.RouteNavigationState) private routeNavigationState: RouteNavigationState,
    @inject(InjectionTokens.FlightState) private flightState: FlightState,
    @inject(InjectionTokens.Logger) private logger: LocalLogger
  ) {}

  private calculateTocAndTodIndexes(routeWaypoints: Waypoint[], distanceToToc: number, distanceToTod: number) {
    let tocIndex: number = null
    let todIndex: number = null

    let distanceOnRoute = 0
    for (let i = 1; i < routeWaypoints.length; i += 1) {
      const previousWaypoint = routeWaypoints[i - 1]
      const currentWpt = routeWaypoints[i]

      const distanceStage = nmDistance(previousWaypoint.coordinates, currentWpt.coordinates)
      distanceOnRoute += distanceStage

      const startOnRouteDistance = distanceOnRoute - distanceStage

      if (startOnRouteDistance <= distanceToToc && distanceOnRoute > distanceToToc) {
        tocIndex = i
      }
      if (!tocIndex) continue
      if (startOnRouteDistance <= distanceToTod && distanceOnRoute > distanceToTod) {
        todIndex = i + 1
      }
    }

    return { tocIndex, todIndex }
  }

  private calculateTimeOnStageInSeconds(
    distanceOnRoute: number,
    distanceStage: number,
    distanceToToc: number,
    distanceToTod: number,
    speedAc: number,
    speedCruise: number,
    speedDc: number
  ) {
    let timeOnTocToStage = 0
    let timeOnCruiseToStage = 0
    let timeOnDescentToStage = 0

    const startOnRouteDistance = distanceOnRoute - distanceStage

    // The waypoint ends before the top of climb
    if (distanceOnRoute <= distanceToToc) {
      timeOnTocToStage = (distanceStage / speedAc) * 3600
      return { timeOnTocToStage, timeOnCruiseToStage, timeOnDescentToStage }

      // The waypoint ends before the top of descent
    } else if (distanceOnRoute <= distanceToTod) {
      let distanceToTocOnStage = 0

      // The waypoint begins before the top of climb
      if (startOnRouteDistance <= distanceToToc) {
        distanceToTocOnStage = distanceToToc - startOnRouteDistance
        timeOnTocToStage = (distanceToTocOnStage / speedAc) * 3600
      }

      timeOnCruiseToStage = ((distanceStage - distanceToTocOnStage) / speedCruise) * 3600
      return { timeOnTocToStage, timeOnCruiseToStage, timeOnDescentToStage }
      // The waypoint ends after the top of descent
    } else {
      let distanceToTocOnStage = 0
      let distanceOnCruise = 0

      // The waypoint begins before the top of climb
      if (startOnRouteDistance <= distanceToToc) {
        distanceToTocOnStage = distanceToToc - startOnRouteDistance
        timeOnTocToStage = (distanceToTocOnStage / speedAc) * 3600

        distanceOnCruise = distanceToTod - distanceToToc
        timeOnCruiseToStage = (distanceOnCruise / speedCruise) * 3600

        // The waypoint begins before the top of descent
      } else if (startOnRouteDistance <= distanceToTod) {
        distanceOnCruise = distanceToTod - startOnRouteDistance
        timeOnCruiseToStage = (distanceOnCruise / speedCruise) * 3600
      }

      timeOnDescentToStage = ((distanceStage - (distanceToTocOnStage + distanceOnCruise)) / speedDc) * 3600
      return { timeOnTocToStage, timeOnCruiseToStage, timeOnDescentToStage }
    }
  }

  private calculateWaypointAltitudeOnRoute(
    firstWaypointAltitude: number,
    cruiseAltitude: number,
    distanceOnRoute: number,
    timeToOnRoute: number,
    timeToTod: number,
    distanceToToc: number,
    distanceToTod: number,
    rateAc: number,
    rateDc: number
  ) {
    if (distanceOnRoute <= distanceToToc) {
      return firstWaypointAltitude + (timeToOnRoute / 60) * rateAc
    } else if (distanceOnRoute <= distanceToTod) {
      return cruiseAltitude
    } else {
      return cruiseAltitude - ((timeToOnRoute - timeToTod) / 60) * rateDc
    }
  }

  private recalculateWaypointsInfo(routeState: RouteStateProps) {
    const response: Map<UniqueEntityID, RouteStateUserToWaypointsInfo> = new Map()

    const routeWaypoints = routeState.activeRoute?.waypoints
    const verticalInfo = this.routeNavigationState.getStateSnapshot()?.verticalInfo
    if (!routeWaypoints) return
    const firstWaypointAltitude = routeWaypoints[0]?.coordinates?.altitude
    const lastWaypointAltitude = routeWaypoints[routeWaypoints.length - 1]?.coordinates?.altitude

    let hasTocAndTod = false
    if (isSet(verticalInfo)) {
      hasTocAndTod = isSet(verticalInfo.distanceToToc) && isSet(verticalInfo.distanceToTod)
    }

    const { activeAircraft, activeCruiseAltitude, avgSpeed, avgFuelConsumption } = this.flightState.getStateSnapshot()

    let distanceOnRoute = 0
    let timeToOnRoute = 0
    let fuelConsumptionOnRoute = 0

    for (let i = 1; i < routeWaypoints.length; i++) {
      const lastWaypoint = routeWaypoints[i - 1]

      const bearing = magneticBearing(lastWaypoint.coordinates, routeWaypoints[i].coordinates)
      const routeDirection = magneticBearing(routeWaypoints[0].coordinates, routeWaypoints[i].coordinates)

      const distanceStage = nmDistance(lastWaypoint.coordinates, routeWaypoints[i].coordinates)
      distanceOnRoute += distanceStage

      let timeToStage = null
      let fuelConsumptionStage = null
      // If the route has top of climb and top of descent, calculate the time and fuel consumption for each stage
      if (hasTocAndTod) {
        const { timeOnTocToStage, timeOnCruiseToStage, timeOnDescentToStage } = this.calculateTimeOnStageInSeconds(
          distanceOnRoute,
          distanceStage,
          verticalInfo.distanceToToc,
          verticalInfo.distanceToTod,
          activeAircraft.speedAc,
          activeAircraft.speedCruise,
          activeAircraft.speedDc
        )
        timeToStage = timeOnTocToStage + timeOnCruiseToStage + timeOnDescentToStage
        timeToOnRoute += timeToStage

        fuelConsumptionStage =
          calculateFuelConsumptionPerHour(timeOnTocToStage, activeAircraft.fuelConsumptionAc) +
          calculateFuelConsumptionPerHour(timeOnCruiseToStage, activeAircraft.fuelConsumptionCruise) +
          calculateFuelConsumptionPerHour(timeOnDescentToStage, activeAircraft.fuelConsumptionDc)
        fuelConsumptionOnRoute += fuelConsumptionStage
      }
      // else, calculate the time and fuel consumption for the stage using the cruise speed
      else {
        timeToStage = avgSpeed ? (distanceStage / avgSpeed) * 3600 : null
        timeToOnRoute += timeToStage

        fuelConsumptionStage = avgFuelConsumption
          ? calculateFuelConsumptionPerHour(timeToStage, avgFuelConsumption)
          : null

        fuelConsumptionOnRoute += fuelConsumptionStage
      }

      const altitudeOnRoute = hasTocAndTod
        ? this.calculateWaypointAltitudeOnRoute(
            firstWaypointAltitude,
            activeCruiseAltitude,
            distanceOnRoute,
            timeToOnRoute,
            verticalInfo.timeToTod,
            verticalInfo.distanceToToc,
            verticalInfo.distanceToTod,
            activeAircraft.rateAc,
            activeAircraft.rateDc
          )
        : null

      response.set(routeWaypoints[i].id, {
        bearing,
        routeDirection,

        distanceStage,
        timeToStage,
        fuelConsumptionStage,
        etaStage: null,

        altitudeOnRoute,
        distanceOnRoute,
        timeToOnRoute,
        fuelConsumptionOnRoute,
        etaOnRoute: null
      })
    }

    response.set(routeWaypoints[0]?.id, {
      ...response.get(routeWaypoints[0]?.id),
      altitudeOnRoute: firstWaypointAltitude
    })

    response.set(routeWaypoints[routeWaypoints.length - 1]?.id, {
      ...response.get(routeWaypoints[routeWaypoints.length - 1]?.id),
      altitudeOnRoute: lastWaypointAltitude
    })

    this.routeNavigationState.setUserToWaypointInfo(response)
  }

  private recalculateVerticalInfo(routeState: RouteStateProps, flightState: FlightStateProps) {
    let response: RouteStateVerticalInfo = null

    const routeWaypoints = routeState.activeRoute?.waypoints
    const activeAircraft = flightState.activeAircraft
    const activeCruiseAltitude = flightState.activeCruiseAltitude

    if (!routeWaypoints) return
    if (routeWaypoints.length < 2) return
    if (!activeAircraft || !activeCruiseAltitude) return

    const firstWaypoint = routeWaypoints[0]
    const lastWaypoint = routeWaypoints[routeWaypoints.length - 1]

    const firstWaypointAltitude = firstWaypoint.coordinates?.altitude
    const lastWaypointAltitude = lastWaypoint.coordinates?.altitude

    const routeTotalDistance = routeState.activeRoute.totalDistance

    if (!isSet(firstWaypointAltitude) || !isSet(lastWaypointAltitude) || !isSet(routeTotalDistance)) return

    // On Climb calculations
    const timeToToc = calculateEteToTocInSeconds(
      firstWaypointAltitude,
      flightState.activeCruiseAltitude,
      activeAircraft.rateAc
    )

    const distanceToToc = calculateDistanceToTocInNM(timeToToc, activeAircraft.speedAc)
    const fuelConsumptionToToc = calculateFuelConsumptionPerHour(timeToToc, activeAircraft.fuelConsumptionAc)

    // On Cruise calculations
    const distanceToTod = calculateDistanceToTodInNM(
      flightState.activeCruiseAltitude,
      lastWaypointAltitude,
      activeAircraft.rateDc,
      activeAircraft.speedDc,
      routeTotalDistance
    )

    const timeOnCruise = calculateEteFromTocToTodInSeconds(distanceToTod - distanceToToc, activeAircraft.speedCruise)
    const fuelConsumptionOnCruise = calculateFuelConsumptionPerHour(timeOnCruise, activeAircraft.fuelConsumptionCruise)

    const timeToTod = timeToToc + timeOnCruise
    const fuelConsumptionToTod = fuelConsumptionToToc + fuelConsumptionOnCruise

    const { tocIndex, todIndex } = this.calculateTocAndTodIndexes(routeWaypoints, distanceToToc, distanceToTod)

    response = {
      distanceToToc,
      timeToToc,
      fuelConsumptionToToc,
      etaToToc: null,

      distanceToTod,
      timeToTod,
      fuelConsumptionToTod,
      etaToTod: null,

      tocIndex,
      todIndex
    }

    this.routeNavigationState.setVerticalInfo(response)
  }

  private resetUserToWaypointInfo() {
    this.routeNavigationState.setUserToWaypointInfo(null)
  }

  private resetVerticalPlanningInfo() {
    this.routeNavigationState.setVerticalInfo(null)
  }

  private resetPlanningInfo() {
    this.resetUserToWaypointInfo()
    this.resetVerticalPlanningInfo()
  }

  private tryRecalculateWaypointsInfo(routeState: RouteStateProps) {
    try {
      this.recalculateWaypointsInfo(routeState)
    } catch (error) {
      this.logger.error('RouteStateSideEffects', `${error}`)
    }
  }

  private tryRecalculateVerticalInfo(routeState: RouteStateProps, flightState: FlightStateProps) {
    try {
      this.recalculateVerticalInfo(routeState, flightState)
    } catch (error) {
      this.logger.error('RouteStateSideEffects', `${error}`)
    }
  }

  init(): Result<void> {
    try {
      this.start()
      return Result.ok()
    } catch (error) {
      this.logger.error('RouteStateSideEffects', `Initialization failed: ${error}`)
      return Result.fail('Initialization failed')
    }
  }

  start(): Result<void> {
    if (this.changeRouteSubject && this.changeFlightStateSubject) {
      return Result.fail('RouteStateSideEffects service already started')
    }

    this.logger.log('RouteStateSideEffects', 'Starting service')

    this.changeRouteSubject = this.routeState.getState().subscribe({
      next: async (routeState) => {
        let shouldTriggerRecalculation = false
        const flightStateSnapshot = this.flightState.getStateSnapshot()
        const verticalInfo = this.routeNavigationState.getStateSnapshot().verticalInfo

        if (routeState.activeRoute === null) {
          this.resetPlanningInfo()
          return
        }

        const routeWaypoints = routeState.activeRoute.waypoints
        if (!routeWaypoints[0].coordinates.altitude) {
          const result = await new GetWaypointAltitudeUseCase().execute(routeWaypoints[0].coordinates)
          if (result.isSuccess) {
            routeWaypoints[0].coordinates.altitude = result.getValue()
            shouldTriggerRecalculation = true
          }
        }

        if (routeWaypoints.length >= 2 && !routeWaypoints[routeWaypoints.length - 1].coordinates.altitude) {
          const result = await new GetWaypointAltitudeUseCase().execute(
            routeWaypoints[routeWaypoints.length - 1].coordinates
          )
          if (result.isSuccess) {
            routeWaypoints[routeWaypoints.length - 1].coordinates.altitude = result.getValue()
            shouldTriggerRecalculation = true
          }
        }

        const firstWaypointAltitude = routeWaypoints[0].coordinates.altitude
        const lastWaypointAltitude = routeWaypoints[routeWaypoints.length - 1].coordinates.altitude

        if (verticalInfo && (!isSet(firstWaypointAltitude) || !isSet(lastWaypointAltitude))) {
          this.resetVerticalPlanningInfo()
        } else {
          this.tryRecalculateVerticalInfo(routeState, flightStateSnapshot)
        }

        this.tryRecalculateWaypointsInfo(routeState)

        if (shouldTriggerRecalculation && this.altitudeRecalculationCount < this.maxAltitudeRecalculationCount) {
          this.altitudeRecalculationCount += 1
          this.routeState.updateState(routeState)
        } else if (this.altitudeRecalculationCount >= this.maxAltitudeRecalculationCount) {
          this.altitudeRecalculationCount = 0
        }

        const infosSimulated = simulateWaypointsInfo(
          firstWaypointAltitude,
          lastWaypointAltitude,
          flightStateSnapshot.activeAircraft,
          flightStateSnapshot.activeCruiseAltitude,
          routeState.activeRoute.totalDistance
        )

        if (infosSimulated?.distanceToTod <= infosSimulated?.distanceToToc) {
          this.logger.error('RouteStateSideEffects', 'Invalid cruise altitude')
          // resetar a altitude de cruzeiro
          this.flightState.updateState({
            activeCruiseAltitude: null
          })
        }
      }
    })

    this.changeFlightStateSubject = this.flightState.getState().subscribe({
      next: (flightState) => {
        const routeStateSnapshot = this.routeState.getStateSnapshot()
        const verticalInfo = this.routeNavigationState.getStateSnapshot().verticalInfo

        if (verticalInfo && (!isSet(flightState.activeAircraft) || !isSet(flightState.activeCruiseAltitude))) {
          this.resetVerticalPlanningInfo()
        } else {
          this.tryRecalculateVerticalInfo(routeStateSnapshot, flightState)
        }

        this.tryRecalculateWaypointsInfo(routeStateSnapshot)
      }
    })

    return Result.ok()
  }

  stop(): Result<void> {
    this.logger.log('[RouteStateSIdeEffects]', 'Stopping service')

    if (this.changeRouteSubject) {
      this.changeRouteSubject.unsubscribe()
      this.changeRouteSubject = null
    }

    if (this.changeFlightStateSubject) {
      this.changeFlightStateSubject.unsubscribe()
      this.changeFlightStateSubject = null
    }

    return Result.ok()
  }
}
